import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TransactionType } from '@/types/finance';
import { useFinance } from '@/contexts/FinanceContext';
import { normalizeName } from '../parsers/contaAzulParser';
import type { CategoryMapping, CostCenterMapping, MappingDecision } from '../types';

type MappingType = 'category' | 'costCenter';

interface Props {
  mappingType: MappingType;
  /** Para categorias, é o type inferido pelo importType (income/expense) */
  categoryType?: TransactionType;
  mappings: (CategoryMapping | CostCenterMapping)[];
  onUpdate: (mappings: (CategoryMapping | CostCenterMapping)[]) => void;
  onBack: () => void;
  onNext: () => void;
}

export function MappingStep({
  mappingType,
  categoryType,
  mappings,
  onUpdate,
  onBack,
  onNext,
}: Props) {
  const { categories, costCenters } = useFinance();

  const title = mappingType === 'category' ? 'Mapear categorias' : 'Mapear centros de custo';
  const existingOptions =
    mappingType === 'category'
      ? categories.filter((c) => c.type === categoryType)
      : costCenters.filter((cc) => cc.isActive);

  const setDecision = (name: string, decision: MappingDecision) => {
    onUpdate(mappings.map((m) => (m.name === name ? { ...m, decision } : m)));
  };

  if (mappings.length === 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Nenhum {mappingType === 'category' ? 'categoria' : 'centro de custo'} encontrado no
          arquivo.
        </p>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Voltar
          </Button>
          <Button onClick={onNext}>Próximo</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-brand text-base font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">
          Para cada item do arquivo, escolha: criar novo, mapear para um existente, ou ignorar.
        </p>
      </div>

      <div className="max-h-[400px] space-y-3 overflow-y-auto pr-2">
        {mappings.map((m) => {
          const decisionKind = m.decision.kind === 'map' ? 'map' : m.decision.kind;
          const matchedExisting = existingOptions.find(
            (o) => normalizeName(o.name) === normalizeName(m.name),
          );
          return (
            <div key={m.name} className="rounded-lg border border-border bg-white p-3">
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-sm font-semibold text-foreground">{m.name}</Label>
                {matchedExisting && (
                  <span className="rounded-full bg-income/10 px-2 py-0.5 text-xs font-medium text-income">
                    Já existe — mapeando
                  </span>
                )}
              </div>
              <RadioGroup
                value={decisionKind}
                onValueChange={(v) => {
                  if (v === 'create') setDecision(m.name, { kind: 'create' });
                  else if (v === 'skip') setDecision(m.name, { kind: 'skip' });
                  else if (v === 'map' && existingOptions[0]) {
                    setDecision(m.name, { kind: 'map', targetId: existingOptions[0].id });
                  }
                }}
                className="flex flex-col gap-1.5"
              >
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value="create" />
                  Criar novo com este nome
                </label>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <RadioGroupItem value="map" disabled={existingOptions.length === 0} />
                  Mapear para existente
                </label>
                {m.decision.kind === 'map' && (
                  <div className="ml-6">
                    <Select
                      value={m.decision.targetId}
                      onValueChange={(v) => setDecision(m.name, { kind: 'map', targetId: v })}
                    >
                      <SelectTrigger className="h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {existingOptions.map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            {o.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
                  <RadioGroupItem value="skip" />
                  Ignorar (sem {mappingType === 'category' ? 'categoria' : 'centro de custo'})
                </label>
              </RadioGroup>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Voltar
        </Button>
        <Button onClick={onNext}>Próximo</Button>
      </div>
    </div>
  );
}
